import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Shield, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Reset failed. The link may have expired.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-[#A3A3A3]">Invalid reset link.</p>
          <Link to="/login" className="text-[#FF3B30] text-sm mt-4 inline-block">Back to Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Shield className="w-6 h-6 text-[#FF3B30]" />
          <span className="font-heading text-white font-bold text-xl tracking-tight">RiskCoach<span className="text-[#FF3B30]">.ai</span></span>
        </div>
        <div className="bg-[#141414] border border-[#262626] rounded-sm p-8">
          {done ? (
            <div className="text-center">
              <div className="w-12 h-12 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-[#22C55E]" />
              </div>
              <h2 data-testid="reset-success" className="text-white font-bold text-xl mb-2">Password Reset</h2>
              <p className="text-[#A3A3A3] text-sm mb-6">Your password has been updated.</p>
              <Link
                to="/login"
                data-testid="reset-login-link"
                className="inline-block bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold px-8 py-3 rounded-sm transition-colors"
              >
                Sign In
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-white font-bold text-xl mb-2 text-center">Set New Password</h2>
              <p className="text-[#A3A3A3] text-sm text-center mb-6">Choose a strong password for your account.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-[#A3A3A3] text-xs font-bold tracking-[0.15em] uppercase block mb-1.5">New Password</label>
                  <div className="relative">
                    <input
                      data-testid="reset-password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white text-sm placeholder:text-[#525252] focus:outline-none focus:ring-1 focus:ring-[#FF3B30]"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#525252] hover:text-white">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[#A3A3A3] text-xs font-bold tracking-[0.15em] uppercase block mb-1.5">Confirm Password</label>
                  <input
                    data-testid="reset-confirm-input"
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter password"
                    required
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm px-4 py-2.5 text-white text-sm placeholder:text-[#525252] focus:outline-none focus:ring-1 focus:ring-[#FF3B30]"
                  />
                </div>
                {error && <p data-testid="reset-error" className="text-[#FF3B30] text-xs">{error}</p>}
                <button
                  data-testid="reset-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FF3B30] hover:bg-[#D62828] disabled:opacity-60 text-white font-bold py-3 rounded-sm transition-colors"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
